import React from "react";

import styled from "@emotion/styled";
import moment from "moment";

import PropTypes from "prop-types";

import Icon from "shared/components/Icon";
import Tile from "shared/components/Tile";
import { RegularText } from "shared/components/Typography";
import { shouldShowSentilink } from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";
import {
  AccordionVerificationStatus,
  IdVerificationDetails,
  NoVerificationsPlaceholder,
} from "shared/components/Verification";
import {
  StatusLabel,
  StatusLeftContainer,
  StatusWrapper,
} from "shared/components/Verification/styled";
import {
  VERIFICATION_ACCORDION_FOR_STATUS,
  normalizePersonaStatus,
} from "shared/components/Verification/utils";
import { COLORS, VERIFICATION_TYPE } from "shared/config/constants";
import { PERSONA_VERIFICATION_TYPES } from "shared/config/constants/integrations.constants";
import { usePermissions } from "shared/hooks";
import { isPersonaIncomplete } from "shared/utils/persona";
import { idVerificationPropType } from "shared/utils/propTypes";
import {
  SENTILINK_ERROR_COPY,
  SENTILINK_NO_REASONS_COPY,
  formatSentilinkAccordionTitle,
  formatSentilinkCheckReasonLabel,
  formatSentilinkEmptyCheckCopy,
  getSentilinkAccordionPresentation,
  getSentilinkCheckReasonGroups,
  getSentilinkReasonDescriptions,
} from "./verificationsSentilink.utils";
import {
  SentilinkBadge,
  SentilinkDetailBody,
  SentilinkDetailDivider,
  SentilinkDetailLabel,
  SentilinkDetailRow,
  SentilinkDetailValue,
  SentilinkHeaderText,
  SentilinkNoReasons,
  SentilinkSubtitle,
  SubTileCustom,
  TileCustom,
  VerificationContainer,
} from "./styled";

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const SENTILINK_STATUS_ICONS = {
  verified: Icon.CheckGreenBig,
  warn: Icon.OrangeExclamation,
  unverified: Icon.CrossRedBig,
};

const SENTILINK_STATUS_COLORS = {
  verified: COLORS.accentDark,
  warn: COLORS.midOrange,
  unverified: COLORS.failedRed,
};

const SENTILINK_BADGE_STYLES = {
  verified: {
    background: COLORS.lightGreen,
    color: COLORS.accentDark,
  },
  warn: {
    background: "#FFF4E5",
    color: COLORS.midOrange,
  },
  unverified: {
    background: COLORS.well?.backgroundLightError || "#FDECEC",
    color: COLORS.failedRed,
  },
  neutral: {
    background: COLORS.lightGray2,
    color: COLORS.textColorSecondary,
  },
};

const formatSentilinkSubtitle = (calledAt) => {
  if (!calledAt) {
    return "Risk assessment";
  }
  const formatted = moment(calledAt).format("MMM D, YYYY · h:mm A");
  return `Risk assessment · ${formatted}`;
};

const Verifications = ({
  idVerifications,
  applicationSummary,
  personaInquiryStatus,
  personaStatus,
  sentilink,
  applicantWorkflow,
  applicantWorkflowLoading = false,
  applicantWorkflowLookupFailed = false,
}) => {
  const { isIdFraudRiskEnabled } = usePermissions();
  const submittedName = [
    sentilink?.submittedFirstName,
    sentilink?.submittedLastName,
  ]
    .filter((part) => typeof part === "string" && part.trim())
    .map((part) => part.trim())
    .join(" ");
  // If persona is incomplete, show pending state instead of stale verification data
  const isIncomplete = isPersonaIncomplete(personaStatus);
  // Utility function to safely get verification date with defensive handling
  const getVerificationDate = (verification, formatForDisplay = false) => {
    const completedAt =
      verification.verificationDate || verification.attributes?.completedAt;

    if (!completedAt) {
      return "Verification in progress...";
    }

    // If formatForDisplay is true, slice the date for display (YYYY-MM-DD format)
    if (formatForDisplay && typeof completedAt === "string") {
      return completedAt.slice(0, 10);
    }

    return completedAt;
  };

  // Check if verification is complete (has completedAt)
  const isVerificationComplete = (verification) => {
    const completedAt =
      verification.verificationDate || verification.attributes?.completedAt;
    return !!completedAt;
  };

  // Check if verification is AAMVA type
  const isAamvaVerification = (verification) => {
    return verification?.type === PERSONA_VERIFICATION_TYPES.AAMVA;
  };

  const verificationHeader = (verification, verificationType) => {
    return (
      <AccordionVerificationStatus
        type={verificationType}
        verificationType={verification?.type}
        status={verification.status || verification.attributes?.status}
        verificationDate={getVerificationDate(verification)}
        subHeader
      />
    );
  };

  const formatIdVerification = (verification) => {
    // const isAamva = isAamvaVerification(verification);
    // const isComplete = isVerificationComplete(verification);

    // Helper function to mask AAMVA fields when incomplete
    const getFieldValue = (value) => {
      // TODO: Uncomment or Remove this when we have a confirmation on path forward
      // if (isAamva && !isComplete) {
      //   return "[MASKED]";
      // }
      return value;
    };

    return {
      details: {
        checks: verification.attributes.checks,
        firstName: getFieldValue(verification.attributes.nameFirst),
        middleName: getFieldValue(verification.attributes.nameMiddle),
        lastName: getFieldValue(verification.attributes.nameLast),
        dateOfBirth: getFieldValue(verification.attributes.birthdate),
        age: getFieldValue(
          moment().diff(verification.attributes.birthdate, "years")
        ),
        phoneNumber: getFieldValue(verification.attributes.phoneNumber),
        documentClass: getFieldValue(verification.attributes.idClass),
        identificationNumber: getFieldValue(
          verification.attributes.identificationNumber
        ),
        expirationDate: getFieldValue(verification.attributes.expirationDate),
        addressStreet: getFieldValue(verification.attributes.addressStreet1),
        addressCity: getFieldValue(verification.attributes.addressCity),
        addressSubdivisionAbbr: getFieldValue(
          verification.attributes.addressSubdivision
        ),
        addressPostalCodeAbbr: getFieldValue(
          verification.attributes.addressPostalCode
        ),
        addressCountryCode: getFieldValue(verification.attributes.countryCode),
        verificationFiles: verification.verificationFiles,
        issuingAuthority: getFieldValue(
          verification.attributes.issuingAuthority
        ),
        issueDate: getFieldValue(verification.attributes.issueDate),
      },
    };
  };

  const idVerificationContent = (verification) => {
    return (
      <Tile.Inner>
        <IdVerificationDetails
          verification={formatIdVerification(verification)}
          phoneNumber={applicationSummary?.phoneNumber}
          verificationType={verification?.type}
        />
      </Tile.Inner>
    );
  };

  const renderVerification = (verification, verificationType, index) => {
    const content = idVerificationContent(verification);
    return (
      <SubTileCustom
        header={{
          title: verificationHeader(verification, verificationType),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
          defaultCollapsed: index !== 0,
        }}
        noBorderTop
        noBorderLeft
        noBorderRight
        noMarginBottom
      >
        <VerificationContainer>{content}</VerificationContainer>
      </SubTileCustom>
    );
  };

  const verificationStatus = (status) => {
    // Persona status takes precedence over inquiry status
    if (isIncomplete) {
      return "pending";
    }
    return normalizePersonaStatus(status);
  };

  const dropHeader = (verification, verificationType) => {
    return (
      <HeaderContainer>
        <HeaderLeft className="main-header-left">
          <AccordionVerificationStatus
            type={verificationType}
            status={verificationStatus(personaInquiryStatus)}
            verificationDate={isIncomplete ? null : getVerificationDate(verification, true)}
          />
        </HeaderLeft>
      </HeaderContainer>
    );
  };

  const verificationContent = ({ verifications, verificationType }) => {
    return (
      <TileCustom
        className={
          VERIFICATION_ACCORDION_FOR_STATUS[
            verificationStatus(personaInquiryStatus)
          ]
        }
        header={{
          title: dropHeader(verifications[0], verificationType),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
        }}
      >
        <Tile key={0} noBorder noMarginBottom>
          {verifications.map((verification, index) => {
            return renderVerification(verification, verificationType, index);
          })}
        </Tile>
      </TileCustom>
    );
  };

  const renderSentilinkDetailRow = (label, value, valueColorKey, rowKey) => {
    const valueColor =
      valueColorKey && SENTILINK_STATUS_COLORS[valueColorKey]
        ? SENTILINK_STATUS_COLORS[valueColorKey]
        : COLORS.textColorPrimary;

    return (
      <SentilinkDetailRow key={rowKey}>
        <SentilinkDetailLabel>{label}</SentilinkDetailLabel>
        <SentilinkDetailValue valueColor={valueColor}>{value}</SentilinkDetailValue>
      </SentilinkDetailRow>
    );
  };

  const renderSentilinkHeader = (presentation, calledAt, title) => {
    const StatusIcon = presentation.statusIconKey
      ? SENTILINK_STATUS_ICONS[presentation.statusIconKey]
      : null;
    const statusColor = presentation.statusIconKey
      ? SENTILINK_STATUS_COLORS[presentation.statusIconKey]
      : COLORS.textColorPrimary;
    const badgeStyleKey =
      presentation.valueColorKey || presentation.statusIconKey || "neutral";
    const badgeStyle =
      SENTILINK_BADGE_STYLES[badgeStyleKey] || SENTILINK_BADGE_STYLES.neutral;

    return (
      <HeaderContainer>
        <HeaderLeft className="main-header-left">
          <StatusWrapper>
            <StatusLeftContainer>
              {StatusIcon && (
                <StatusIcon width="24" height="24" className="status-icon" />
              )}
              <SentilinkHeaderText>
                <StatusLabel color={statusColor} fontWeight={600}>
                  {title ||
                    formatSentilinkAccordionTitle(presentation.statusSuffix)}
                </StatusLabel>
                {sentilink?.triggered && (
                  <SentilinkSubtitle>
                    {formatSentilinkSubtitle(calledAt)}
                  </SentilinkSubtitle>
                )}
              </SentilinkHeaderText>
            </StatusLeftContainer>
            {presentation.badgeLabel && (
              <SentilinkBadge
                background={badgeStyle.background}
                color={badgeStyle.color}
              >
                {presentation.badgeLabel}
              </SentilinkBadge>
            )}
          </StatusWrapper>
        </HeaderLeft>
      </HeaderContainer>
    );
  };

  const renderSentilinkDetailRows = (rows) =>
    rows.map((row, index) => (
      <React.Fragment key={row.key}>
        {index > 0 && <SentilinkDetailDivider />}
        {renderSentilinkDetailRow(
          row.label,
          row.value,
          row.valueColorKey,
          row.key
        )}
      </React.Fragment>
    ));

  const renderSentilinkCheckGroup = (group) => {
    if (!group.descriptions.length) {
      return (
        <SentilinkDetailBody
          key={`${group.key}-empty`}
          data-testid={`sentilink-${group.key}-empty`}
        >
          <RegularText>{formatSentilinkEmptyCheckCopy(group.label)}</RegularText>
        </SentilinkDetailBody>
      );
    }

    return (
      <SubTileCustom
        key={group.key}
        data-testid={`sentilink-${group.key}-subtile`}
        header={{
          title: formatSentilinkCheckReasonLabel(
            group.label,
            group.descriptions.length
          ),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
          defaultCollapsed: true,
        }}
        noBorderTop
        noBorderLeft
        noBorderRight
        noMarginBottom
      >
        <VerificationContainer>
          <SentilinkDetailBody>
            {renderSentilinkDetailRows(
              group.descriptions.map((description, index) => ({
                key: `${group.key}-reason-${index}`,
                label:
                  group.descriptions.length > 1
                    ? `Reason ${index + 1}`
                    : "Reason",
                value: description,
                valueColorKey: null,
              }))
            )}
          </SentilinkDetailBody>
        </VerificationContainer>
      </SubTileCustom>
    );
  };

  const renderLegacySentilinkSection = () => {
    const presentation = getSentilinkAccordionPresentation(sentilink);
    if (!presentation) {
      return null;
    }

    if (!sentilink.triggered) {
      const notTriggeredBody =
        sentilink.notTriggeredStatus === "SKIPPED_NO_SSN"
          ? sentilink.noUsSsnReason?.trim()
            ? `Applicant skipped identity risk check: ${sentilink.noUsSsnReason.trim()}`
            : "Applicant indicated they do not have a US SSN. Identity risk check was not run."
          : "Identity risk check has not been run for this application yet.";

      return (
        <TileCustom
          header={{
            title: renderSentilinkHeader(presentation, sentilink.calledAt),
            collapsibleOnDesktop: true,
            collapsibleOnMobile: true,
            defaultCollapsed: presentation.defaultCollapsed,
          }}
        >
          <VerificationContainer>
            <SentilinkDetailBody>
              <RegularText>{notTriggeredBody}</RegularText>
            </SentilinkDetailBody>
          </VerificationContainer>
        </TileCustom>
      );
    }

    const reasonDescriptions = presentation.showReasons
      ? getSentilinkReasonDescriptions(sentilink)
      : [];

    const detailRows = [];

    if (presentation.riskLevelValue) {
      detailRows.push({
        key: "risk-level",
        label: "Risk Level",
        value: presentation.riskLevelValue,
        valueColorKey: presentation.valueColorKey,
      });
    }

    if (isIdFraudRiskEnabled && submittedName) {
      detailRows.push({
        key: "submitted-name",
        label: "Submitted Name",
        value: submittedName,
        valueColorKey: null,
      });
    }

    reasonDescriptions.forEach((description, index) => {
      detailRows.push({
        key: `reason-${index}`,
        label: reasonDescriptions.length > 1 ? `Reason ${index + 1}` : "Reason",
        value: description,
        valueColorKey: null,
      });
    });

    if (sentilink.transactionId) {
      detailRows.push({
        key: "transaction-id",
        label: "Transaction ID",
        value: sentilink.transactionId,
        valueColorKey: null,
      });
    }

    if (sentilink.calledAt) {
      detailRows.push({
        key: "checked-at",
        label: "Checked At",
        value: moment(sentilink.calledAt).format("MMM D, YYYY · h:mm A"),
        valueColorKey: null,
      });
    }

    return (
      <TileCustom
        className={presentation.tileClass}
        header={{
          title: renderSentilinkHeader(presentation, sentilink.calledAt),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
          defaultCollapsed: presentation.defaultCollapsed,
        }}
      >
        <VerificationContainer>
          <SentilinkDetailBody>
            {renderSentilinkDetailRows(detailRows)}
          </SentilinkDetailBody>
        </VerificationContainer>
      </TileCustom>
    );
  };

  /** Synthetic Identity score (PRODUCT-2222) — review only, shown alongside id-theft above. */
  const renderSyntheticSection = () => {
    if (!sentilink?.triggered) {
      return null;
    }

    const hasScore = sentilink.syntheticScore != null;
    const presentation =
      sentilink.syntheticOutcome === "ERROR" ||
      (hasScore && sentilink.syntheticRiskTier)
        ? getSentilinkAccordionPresentation({
            triggered: true,
            outcome: sentilink.syntheticOutcome,
            riskTier: sentilink.syntheticRiskTier,
          })
        : {
            tileClass: "",
            statusSuffix: hasScore ? "" : "Not available",
            badgeLabel: null,
            riskLevelValue: null,
            valueColorKey: null,
            statusIconKey: null,
            defaultCollapsed: true,
          };

    const reasonDescriptions = hasScore
      ? (sentilink.syntheticReasonCodes || [])
          .map((rc) =>
            typeof rc?.description === "string" ? rc.description.trim() : ""
          )
          .filter(Boolean)
      : [];

    const detailRows = [];

    if (hasScore) {
      if (presentation.riskLevelValue) {
        detailRows.push({
          key: "synthetic-risk-level",
          label: "Risk Level",
          value: presentation.riskLevelValue,
          valueColorKey: presentation.valueColorKey,
        });
      }
      detailRows.push({
        key: "synthetic-score",
        label: "Score",
        value: `${sentilink.syntheticScore} / 999`,
      });
      if (sentilink.syntheticScoreVersion) {
        detailRows.push({
          key: "synthetic-score-version",
          label: "Score Version",
          value: sentilink.syntheticScoreVersion,
        });
      }
      reasonDescriptions.forEach((description, index) => {
        detailRows.push({
          key: `synthetic-reason-${index}`,
          label:
            reasonDescriptions.length > 1 ? `Reason ${index + 1}` : "Reason",
          value: description,
        });
      });
    } else if (presentation.riskLevelValue) {
      detailRows.push({
        key: "synthetic-risk-level",
        label: "Risk Level",
        value: presentation.riskLevelValue,
        valueColorKey: presentation.valueColorKey,
      });
    } else {
      detailRows.push({
        key: "synthetic-not-available",
        label: "Score",
        value: "Not available",
      });
    }

    return (
      <TileCustom
        className={presentation.tileClass}
        header={{
          title: renderSentilinkHeader(
            presentation,
            sentilink.calledAt,
            presentation.statusSuffix
              ? `Synthetic Identity Risk — ${presentation.statusSuffix}`
              : "Synthetic Identity Risk"
          ),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
          defaultCollapsed: presentation.defaultCollapsed,
        }}
      >
        <VerificationContainer>
          <SentilinkDetailBody>
            {renderSentilinkDetailRows(detailRows)}
          </SentilinkDetailBody>
        </VerificationContainer>
      </TileCustom>
    );
  };

  const renderSentilinkSection = () => {
    const presentation = getSentilinkAccordionPresentation(sentilink);
    if (!presentation) {
      return null;
    }

    if (!sentilink.triggered) {
      const notTriggeredBody =
        sentilink.notTriggeredStatus === "SKIPPED_NO_SSN"
          ? sentilink.noUsSsnReason?.trim()
            ? `Applicant skipped identity risk check: ${sentilink.noUsSsnReason.trim()}`
            : "Applicant indicated they do not have a US SSN. Identity risk check was not run."
          : "Identity risk check has not been run for this application yet.";

      return (
        <TileCustom
          data-testid="sentilink-tile"
          header={{
            title: renderSentilinkHeader(presentation, sentilink.calledAt),
            collapsibleOnDesktop: true,
            collapsibleOnMobile: true,
            defaultCollapsed: presentation.defaultCollapsed,
          }}
        >
          <VerificationContainer>
            <SentilinkDetailBody>
              <RegularText>{notTriggeredBody}</RegularText>
            </SentilinkDetailBody>
          </VerificationContainer>
        </TileCustom>
      );
    }

    if (presentation.isError) {
      return (
        <TileCustom
          className={presentation.tileClass}
          data-testid="sentilink-tile"
          header={{
            title: renderSentilinkHeader(presentation, sentilink.calledAt),
            collapsibleOnDesktop: true,
            collapsibleOnMobile: true,
            defaultCollapsed: presentation.defaultCollapsed,
          }}
        >
          <VerificationContainer>
            <SentilinkDetailBody>
              <RegularText>{SENTILINK_ERROR_COPY}</RegularText>
            </SentilinkDetailBody>
          </VerificationContainer>
        </TileCustom>
      );
    }

    const detailRows = [];

    if (presentation.riskLevelValue) {
      detailRows.push({
        key: "risk-level",
        label: "Risk Level",
        value: presentation.riskLevelValue,
        valueColorKey: presentation.valueColorKey,
      });
    }

    if (isIdFraudRiskEnabled && submittedName) {
      detailRows.push({
        key: "submitted-name",
        label: "Submitted Name",
        value: submittedName,
        valueColorKey: null,
      });
    }

    if (presentation.isManuallyVerified) {
      const manual = sentilink.identityManualVerification;
      const reviewer =
        manual?.reviewerName || manual?.reviewerEmail;
      if (reviewer) {
        detailRows.push({
          key: "verified-by",
          label: "Verified By",
          value: reviewer,
          valueColorKey: null,
        });
      }
      if (manual?.verifiedAt) {
        detailRows.push({
          key: "verified-at",
          label: "Verified At",
          value: moment(manual.verifiedAt).format("MMM D, YYYY · h:mm A"),
          valueColorKey: null,
        });
      }
    }

    if (sentilink.transactionId) {
      detailRows.push({
        key: "transaction-id",
        label: "Transaction ID",
        value: sentilink.transactionId,
        valueColorKey: null,
      });
    }

    if (sentilink.calledAt) {
      detailRows.push({
        key: "checked-at",
        label: "Checked At",
        value: moment(sentilink.calledAt).format("MMM D, YYYY · h:mm A"),
        valueColorKey: null,
      });
    }

    const checkGroups = presentation.showReasons
      ? getSentilinkCheckReasonGroups(sentilink)
      : [];

    return (
      <TileCustom
        className={presentation.tileClass}
        data-testid="sentilink-tile"
        header={{
          title: renderSentilinkHeader(presentation, sentilink.calledAt),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
          defaultCollapsed: presentation.defaultCollapsed,
        }}
      >
        <VerificationContainer>
          <SentilinkDetailBody>
            {renderSentilinkDetailRows(detailRows)}
            {!presentation.isManuallyVerified && !presentation.showReasons && (
                <>
                  {detailRows.length > 0 && <SentilinkDetailDivider />}
                  <SentilinkNoReasons>
                    <RegularText>{SENTILINK_NO_REASONS_COPY}</RegularText>
                  </SentilinkNoReasons>
                </>
            )}
          </SentilinkDetailBody>
          {presentation.showReasons &&
            checkGroups.map((group) => renderSentilinkCheckGroup(group))}
        </VerificationContainer>
      </TileCustom>
    );
  };

  const personaBlock = () => {
    if (isIncomplete) {
      return (
        <TileCustom
          className={VERIFICATION_ACCORDION_FOR_STATUS.pending}
          header={{
            title: dropHeader(
              idVerifications?.[0] || {},
              VERIFICATION_TYPE.ID
            ),
            collapsibleOnDesktop: true,
            collapsibleOnMobile: true,
          }}
        >
          <Tile.Inner>
            <NoVerificationsPlaceholder message="ID verification pending" />
          </Tile.Inner>
        </TileCustom>
      );
    }

    const personaVerifications = idVerifications?.slice().reverse();

    return (
      <>
        {(!personaVerifications || personaVerifications?.length === 0) && (
          <NoVerificationsPlaceholder message="No Identity Verifications Available" />
        )}
        {personaVerifications && personaVerifications?.length > 0 && (
          verificationContent({
            verifications: personaVerifications,
            verificationType: VERIFICATION_TYPE.ID,
          })
        )}
      </>
    );
  };

  return (
    <>
      {personaBlock()}
      {shouldShowSentilink(
        sentilink,
        applicantWorkflow,
        applicantWorkflowLoading,
        applicantWorkflowLookupFailed
      ) &&
        (isIdFraudRiskEnabled ? (
          renderSentilinkSection()
        ) : (
          <>
            {renderLegacySentilinkSection()}
            {renderSyntheticSection()}
          </>
        ))}
    </>
  );
};

Verifications.propTypes = {
  idVerifications: PropTypes.arrayOf(idVerificationPropType).isRequired,
  applicationSummary: PropTypes.object,
  personaInquiryStatus: PropTypes.string,
  personaStatus: PropTypes.string,
  sentilink: PropTypes.shape({
    triggered: PropTypes.bool.isRequired,
    notTriggeredStatus: PropTypes.oneOf(["NOT_STARTED", "SKIPPED_NO_SSN"]),
    noUsSsnReason: PropTypes.string,
    reasonCodes: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    outcome: PropTypes.oneOf(["PASS", "REVIEW", "BLOCK", "ERROR", null]),
    riskTier: PropTypes.oneOf(["LOW_RISK", "NEEDS_REVIEW", "HIGH_RISK", null]),
    syntheticScore: PropTypes.number,
    syntheticScoreVersion: PropTypes.string,
    syntheticReasonCodes: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    syntheticOutcome: PropTypes.oneOf(["PASS", "REVIEW", "BLOCK", "ERROR", null]),
    syntheticRiskTier: PropTypes.oneOf(["LOW_RISK", "NEEDS_REVIEW", "HIGH_RISK", null]),
    transactionId: PropTypes.string,
    calledAt: PropTypes.string,
    submittedFirstName: PropTypes.string,
    submittedLastName: PropTypes.string,
    identityManuallyVerified: PropTypes.bool,
    identityManualVerification: PropTypes.shape({
      reviewerName: PropTypes.string,
      reviewerEmail: PropTypes.string,
      verifiedAt: PropTypes.string,
    }),
  }),
  applicantWorkflow: PropTypes.object,
  applicantWorkflowLoading: PropTypes.bool,
  applicantWorkflowLookupFailed: PropTypes.bool,
};

Verifications.defaultProps = {
  applicationSummary: undefined,
};

export default Verifications;
